/**
 * DistributionChart component
 * Requirements: 16.1, 16.2 - Distribution visualization (histogram, box plot)
 */
import { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  ErrorBar,
} from 'recharts';
import type { DistributionStats, HistogramChartData } from '../types';

export interface DistributionChartProps {
  distribution: Record<string, DistributionStats>;
  chartType: 'histogram' | 'boxplot';
  title?: string;
  height?: number;
  showStatistics?: boolean;
}

/**
 * Color palette for different thinking types
 */
const getTypeColor = (index: number): string => {
  const colors = [
    '#2196f3', // blue
    '#4caf50', // green
    '#ff9800', // orange
    '#e91e63', // pink
    '#9c27b0', // purple
    '#00bcd4', // cyan
    '#ff5722', // deep orange
    '#607d8b', // blue grey
  ];
  return colors[index % colors.length];
};

/**
 * Convert histogram data for chart
 */
const convertHistogramData = (
  distribution: Record<string, DistributionStats>
): HistogramChartData[] => {
  const types = Object.keys(distribution);
  if (types.length === 0) return [];

  // Get all unique ranges from first type
  const firstType = types[0];
  const buckets = distribution[firstType]?.histogram || [];

  return buckets.map((bucket) => {
    const dataPoint = {
      range: `${bucket.min}-${bucket.max}`,
      count: 0,
    } as HistogramChartData & Record<string, number | string>;

    types.forEach((type) => {
      const typeBucket = distribution[type]?.histogram?.find(
        (b) => b.min === bucket.min && b.max === bucket.max
      );
      dataPoint[type] = typeBucket?.count || 0;
    });

    return dataPoint;
  });
};

/**
 * Convert to box plot data format
 */
const convertBoxPlotData = (distribution: Record<string, DistributionStats>) => {
  return Object.entries(distribution).map(([type, stats]) => ({
    type,
    min: stats.min,
    q1: stats.mean - stats.stdDev,
    median: stats.median,
    q3: stats.mean + stats.stdDev,
    max: stats.max,
    mean: stats.mean,
  }));
};

export const DistributionChart: React.FC<DistributionChartProps> = ({
  distribution,
  chartType,
  title,
  height = 400,
  showStatistics = false,
}) => {
  const theme = useTheme();
  const types = Object.keys(distribution);

  const histogramData = useMemo(
    () => convertHistogramData(distribution),
    [distribution]
  );

  const boxPlotData = useMemo(
    () => convertBoxPlotData(distribution),
    [distribution]
  );

  if (types.length === 0) {
    return (
      <Paper
        data-testid="distribution-chart"
        sx={{ p: 4, textAlign: 'center' }}
      >
        <Typography data-testid="empty-distribution" color="text.secondary">
          分布データがありません
        </Typography>
      </Paper>
    );
  }

  return (
    <Box data-testid="distribution-chart">
      {title && (
        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}

      {/* Statistics Summary */}
      {showStatistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {types.map((type, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type}>
              <Paper
                sx={{
                  p: 2,
                  borderLeft: `4px solid ${getTypeColor(index)}`,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  タイプ {type}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip
                    size="small"
                    label={`平均: ${distribution[type].mean.toFixed(1)}`}
                  />
                  <Chip
                    size="small"
                    label={`中央値: ${distribution[type].median}`}
                  />
                  <Chip
                    size="small"
                    label={`標準偏差: ${distribution[type].stdDev.toFixed(2)}`}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Histogram Chart */}
      {chartType === 'histogram' && (
        <Box data-testid="histogram-chart" sx={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={histogramData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="range"
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                label={{
                  value: 'スコア範囲',
                  position: 'insideBottom',
                  offset: -5,
                }}
              />
              <YAxis
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                label={{
                  value: '人数',
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Legend />
              {types.map((type, index) => (
                <Bar
                  key={type}
                  dataKey={type}
                  name={`タイプ ${type}`}
                  fill={getTypeColor(index)}
                  opacity={0.8}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Box Plot Chart */}
      {chartType === 'boxplot' && (
        <Box data-testid="boxplot-chart" sx={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={boxPlotData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                label={{
                  value: 'スコア',
                  position: 'insideBottom',
                  offset: -5,
                }}
              />
              <YAxis
                type="category"
                dataKey="type"
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                label={{
                  value: 'タイプ',
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
                formatter={(value: number, name: string) => [
                  value.toFixed(1),
                  name === 'median' ? '中央値' : name === 'mean' ? '平均' : name,
                ]}
              />
              <Legend />
              {/* Box representation using bars */}
              <Bar
                dataKey="median"
                name="中央値"
                fill={theme.palette.primary.main}
                barSize={20}
              >
                <ErrorBar
                  dataKey="q1"
                  width={4}
                  strokeWidth={2}
                  stroke={theme.palette.primary.dark}
                />
              </Bar>
              <Line
                type="monotone"
                dataKey="mean"
                name="平均"
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.secondary.main }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};
